import axios from "axios"
import { useMemo, useRef, useState } from "react"
import { Modal, Table } from "react-bootstrap"
import type {
	I_VehicleLease,
	I_VehicleLeaseCalculationResponse,
	I_VehicleLeaseGridView,
} from "src/common_interfaces/I_VehicleLease"
import Loader from "src/components/common/Loader"
import "./LeaseTable.css"
import "../Overview/LeaseInformation/VehicleLeaseInformation.css"
import moment from "moment"
import { CSVLink } from "react-csv"
import AxiosConfig from "src/utilities/AxiosConfig"
import IconBuilder from "src/utilities/iconBuilder/IconBuilder"
import { fmtDate, fmtRate, fmtUSD } from "../fleetUtility"
import LeaseInformationView from "../LeaseTracking/LeaseInformationView/LeaseInformationView"
import { LEASE_CSV_HEADERS, toLeaseCsvRow } from "./leaseCsv"

type LeaseTableProps = {
	leases: I_VehicleLeaseGridView[]
	loading: boolean
	error: string | null
	searchString: string
	statusFilter: string
}

// Unknown statuses fall through to a neutral chip rather than rendering blank.
const statusClass = (status: string): string => {
	if (status === "Active") return "lease-chip lease-chip-active"
	if (status === "Expired") return "lease-chip lease-chip-expired"
	if (status === "Terminated") return "lease-chip lease-chip-terminated"
	return "lease-chip"
}

// Groups a vehicle's leases together, current lease first. numeric:true so
// unit 4412 precedes 10001. Sorts a copy — the input array is Fleet's state.
const sortLeases = (rows: I_VehicleLeaseGridView[]): I_VehicleLeaseGridView[] =>
	[...rows].sort((a, b) => {
		const byUnit = (a.Lease.UnitNumber ?? "").localeCompare(
			b.Lease.UnitNumber ?? "",
			undefined,
			{ numeric: true },
		)
		return byUnit !== 0
			? byUnit
			: Date.parse(b.Lease.DateReceived) - Date.parse(a.Lease.DateReceived)
	})

/**
 * Lease view of the Vehicles page — one row per lease, including prior and
 * terminated ones. Presentational; Fleet owns the fetch.
 *
 * TotalMonthlyLeasePayment already has MonthlyExpenseOverride folded in by the
 * calculation engine — do not re-apply it here.
 */
export default function LeaseTable({
	leases,
	loading,
	error,
	searchString,
	statusFilter,
}: LeaseTableProps) {
	const [selectedLease, setSelectedLease] = useState<I_VehicleLease | null>(null)
	const [selectedCalculations, setSelectedCalculations] =
		useState<I_VehicleLeaseCalculationResponse | null>(null)
	const [loadingCalculations, setLoadingCalculations] = useState(false)
	const [showLeaseInfo, setShowLeaseInfo] = useState(false)
	const requestedLeaseIdRef = useRef<number | null>(null)

	const rows = useMemo(() => {
		const term = searchString.trim().toLowerCase()
		const filtered = leases.filter(({ Lease: lease }) => {
			const matchesUnit =
				term.length === 0 ||
				(lease.UnitNumber ?? "").toLowerCase().includes(term)
			const matchesStatus =
				statusFilter === "All" || lease.Status === statusFilter
			return matchesUnit && matchesStatus
		})
		return sortLeases(filtered)
	}, [leases, searchString, statusFilter])

	const handleSelectLease = (lease: I_VehicleLease) => {
		requestedLeaseIdRef.current = lease.VehicleLeaseId
		setSelectedLease(lease)
		setSelectedCalculations(null)
		setLoadingCalculations(true)
		setShowLeaseInfo(true)

		axios
			.get<I_VehicleLeaseCalculationResponse>(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${lease.VehicleLeaseId}/calculations`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				if (requestedLeaseIdRef.current !== lease.VehicleLeaseId) return
				setSelectedCalculations(res.data)
				setLoadingCalculations(false)
			})
			.catch((err) => {
				if (requestedLeaseIdRef.current !== lease.VehicleLeaseId) return
				setLoadingCalculations(false)
				AxiosConfig.handleError(err)
			})
	}

	if (loading) {
		return <Loader title="Loading Leases" />
	}

	if (error) {
		return <p className="lease-table-message">{error}</p>
	}

	if (leases.length === 0) {
		return <p className="lease-table-message">No leases found.</p>
	}

	if (rows.length === 0) {
		return (
			<p className="lease-table-message">
				No leases match the current filters.
			</p>
		)
	}

	return (
		<>
			{selectedLease && (
				<Modal
					show={showLeaseInfo}
					onHide={() => setShowLeaseInfo(false)}
					dialogClassName="full-screen-modal lease-preview-modal"
				>
					<Modal.Header closeButton>
						<Modal.Title>Lease Preview</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<LeaseInformationView
							lease={selectedLease}
							calculations={selectedCalculations}
							loading={loadingCalculations}
						/>
					</Modal.Body>
				</Modal>
			)}

			<div className="lease-table-toolbar">
				<p className="lease-table-count">
					Showing {rows.length} {rows.length === 1 ? "lease" : "leases"}
				</p>
				<CSVLink
					data={rows.map((r) => toLeaseCsvRow(r))}
					headers={LEASE_CSV_HEADERS}
					filename={`VehicleLeases_${moment().format("YYYY-MM-DD")}.csv`}
					title="Download lease CSV"
				>
					<IconBuilder extension="csv" size={32} />
				</CSVLink>
			</div>

			<div className="LeaseTable">
				<Table striped bordered hover size="sm">
					<thead>
						<tr>
							<th>Unit #</th>
							<th>State</th>
							<th>Status</th>
							<th>Termination Date</th>
							<th>Date Received</th>
							<th>Lease Length</th>
							<th>1st Payment Month</th>
							<th className="lease-calc-col">Monthly Payment</th>
							<th>Capitalized</th>
							<th>Depreciation</th>
							<th>Interest</th>
							<th>Mgmt Fee</th>
							<th>Maint. Monthly</th>
							<th>Sales Tax Per Month</th>
							<th>Sales Tax Incentive</th>
							<th>Period Zero Expense</th>
							<th>Period Zero Depr.</th>
							<th>End of Lease Charge</th>
							<th>EFM Rate</th>
							<th>Down Payment</th>
							<th>Gain on Prior</th>
						</tr>
					</thead>
					<tbody>
						{rows.map(({ Lease: lease, Calculations: calcs }) => (
							<tr
								key={lease.VehicleLeaseId}
								onClick={() => handleSelectLease(lease)}
							>
								<td>{lease.UnitNumber}</td>
								<td>{lease.State ?? "—"}</td>
								<td>
									<span className={statusClass(lease.Status)}>
										{lease.Status}
									</span>
								</td>
								<td>{fmtDate(lease.LeaseRetirementDate)}</td>
								<td>{fmtDate(lease.DateReceived)}</td>
								<td>{lease.LeaseLength} months</td>
								<td>{fmtDate(lease.MonthOfFirstPayment)}</td>
								<td className="lease-calc-col">
									{fmtUSD(calcs.TotalMonthlyLeasePayment)}
								</td>
								<td>{fmtUSD(lease.CapitalizedAmount)}</td>
								<td>{fmtRate(lease.LeaseDepreciationRate)}</td>
								<td>{fmtRate(lease.InterestRate)}</td>
								<td>{fmtRate(lease.MgmtFeePercent)}</td>
								<td>{fmtUSD(lease.MaintenanceProgramMonthly)}</td>
								<td>{fmtUSD(lease.SalesTaxPerMonth)}</td>
								<td>{fmtUSD(lease.SalesTaxOnIncentive)}</td>
								<td>{fmtUSD(lease.PeriodZeroLeaseExpense)}</td>
								<td>{fmtUSD(lease.PeriodZeroDepreciation)}</td>
								<td>{fmtUSD(lease.EndOfLeaseServiceCharge)}</td>
								<td>{fmtUSD(lease.EFMNegotiatedRate)}</td>
								<td>{fmtUSD(lease.DownPayment)}</td>
								<td>{fmtUSD(lease.GainOnPriorVehicle)}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
		</>
	)
}
